import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
} from "@mui/material";
import {
  Assessment,
  TrendingUp,
  LocalGasStation,
  Build,
  AttachMoney,
  Receipt,
} from "@mui/icons-material";
import MetricCard from "../components/MetricCard";
import { useFirebaseAuth } from "../hooks/useFirebaseAuth";
import axios from "axios";
import { useToast } from "../components/Toast";
import { Loading } from "../components/Loading";
import RecentActivities from "../components/RecentActivities";
import { getValidToken } from "../utils/authUtils";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from "../utils/utils";
import { usePlanStatus } from "../hooks/usePlanStatus";
import PaymentDue from "./payments/PaymentDue";
import { OperationType } from "../types/operationType";
import MaintenanceOverallReport from "./report/components/MaintenanceOverallReport";
import LargeFleetDashboard from "./LargeFleetDashboard";
import { PROJECT_TERMINOLOGY } from "../project/project_module/enums/terminologyConfig";
import { commonButtonProps } from "../utils/styles";
import CompanyActivity, { getActivities } from "../components/CompanyActivity";

interface MetricCardData {
  title: string;
  total: number | string;
  subtitle: string;
  data: Array<{
    value: number;
    label: string;
    color: string;
  }>;
  path?: string;
  hideByDefault?: boolean;
  canView?: boolean;
}

interface DashboardStats {
  company: {
    name: string;
    totalVehicles: number;
    totalStaff: number;
    tripCount: number;
    totalIncome: number;
    totalExpense: number;
    activeTripsCount: number;
    completedTripsCount: number;
  };
  vehicleStatus: {
    Available: number;
    Maintenance?: number;
  };
  staffStatus: {
    ACTIVE: number;
    BLACKLISTED: number;
  };
  projectStatus: {
    activeProjects: number;
    completedProjects: number;
    totalProjects: number;
  };
  rentalStatus: {
    totalRentalJourneys: number;
  };
}

const Dashboard = () => {
  const { user, loading: authLoading } = useFirebaseAuth();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const navigate = useNavigate();
  const { planStatus, blockNavigation } = usePlanStatus();
  const [paymentDue, setPaymentDue] = useState<any>(null);
  const [companyActivity, setCompanyActivity] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(9);

  const fetchDashboardStats = async () => {
    try {
      const token = await getValidToken();
      const url = `${process.env.REACT_APP_API_HOST}dashboard/stats`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.isSuccess) {
        setStats(response.data.data);
      }

      const companyActivity = await getActivities(1, limit);
      setCompanyActivity(companyActivity?.data || []);
      setHasMore(companyActivity?.pagination?.total > limit);
    } catch (error) {
      return;
    } finally {
      setIsLoading(false);
    }
  };

  const handleShowMore = async () => {
    setIsLoadingMore(true);
    try {
      const data = await getActivities(1, limit + 10); // Load 15 total records (5 + 10 more)
      setCompanyActivity(data?.data || []);
      setHasMore(data?.pagination?.total > limit + 10);
      setLimit(limit + 10);
    } catch (error) {
      console.error("Error loading more activities:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };
  const fetchDuePayments = async () => {
    try {
      const token = await getValidToken();
      const url = `${process.env.REACT_APP_API_HOST}payments/active-orders`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data?.isSuccess) {
        // if payment is overdue show account is locked
        const payment = response.data.data[0];

        const dueDate = new Date(payment.paymentDueDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        dueDate.setHours(0, 0, 0, 0);
        const daysUntilDue = Math.floor(
          (dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
        );
        if (daysUntilDue < 0) {
          // Navigate to account locked page
          navigate("/account-locked", {
            state: {
              paymentDue: {
                isSuccess: response.data.isSuccess,
                data: response.data.data,
              },
            },
          });
          return; // Stop further execution
        }

        const paymentData = {
          isSuccess: response.data.isSuccess,
          data: response.data.data,
        };
        setPaymentDue(paymentData);
      }
    } catch (error) {
      console.error("Error fetching due payments:", error);
    }
  };

  useEffect(() => {
    if (!authLoading && user) {
      fetchDashboardStats();
      fetchDuePayments();
    }
  }, [user, authLoading]);

  const {
    canViewIncomeReport,
    canViewExpenseReport,
    canViewVehicles,
    canViewDrivers,
    canViewTrips,
  } = React.useMemo(() => {
    return {
      canViewIncomeReport:
        planStatus?.permissions?.canViewIncomeReport || false,
      canViewExpenseReport:
        planStatus?.permissions?.canViewExpenseReport || false,
      canViewVehicles: planStatus?.permissions?.canViewVehicles || false,
      canViewDrivers: planStatus?.permissions?.canViewDriver || false,
      canViewTrips: planStatus?.permissions?.canViewTrips || false,
    };
  }, [planStatus?.permissions]);

  if (authLoading || isLoading) {
    return <Loading message="Loading dashboard..." fullScreen={false} />;
  }

  if (!stats) {
    return null;
  }

  // Format data for metric cards
  const vehicleData: MetricCardData = {
    title: "Fleet / Gaadi",
    total: stats.company.totalVehicles,
    subtitle: "Vehicles",
    data: [
      {
        value: stats.vehicleStatus.Available,
        label: "Available",
        color: "#4caf50",
      },
      {
        value:
          stats.vehicleStatus.Maintenance ||
          stats.company.totalVehicles - stats.vehicleStatus.Available ||
          0,
        label: "Maintenance",
        color: "#ff9800",
      },
    ],
    path: "/vehicles",
    hideByDefault: false,
    canView: canViewVehicles,
  };

  const driverData: MetricCardData = {
    title: "Driver",
    total: stats.company.totalStaff,
    subtitle: "Drivers",
    data: [
      { value: stats.staffStatus.ACTIVE, label: "Active", color: "#4caf50" },
      {
        value: stats.staffStatus.BLACKLISTED || 0,
        label: "Blacklisted",
        color: "#f44336",
      },
    ],
    path: "/master-data/staff/DRIVER",
    hideByDefault: false,
    canView: canViewDrivers,
  };

  const revenueData: MetricCardData = {
    title: "Total Income",
    total: formatCurrency(stats.company.totalIncome),
    subtitle: "Income",
    data: [],
    path: "/reports/income",
    hideByDefault: true,
    canView: canViewIncomeReport,
  };

  const expenseData: MetricCardData = {
    title: "Total Expense",
    total: formatCurrency(stats.company.totalExpense),
    subtitle: "Expense",
    data: [],
    path: "/reports/expense",
    hideByDefault: true,
    canView: canViewExpenseReport,
  };

  const tripData: MetricCardData = {
    title: "Trips",
    total: stats.company.tripCount,
    subtitle: "Total Trips",
    data: [
      {
        value: stats.company.activeTripsCount,
        label: "Active",
        color: "#4caf50",
      },
      {
        value: stats.company.completedTripsCount,
        label: "Completed",
        color: "#ff9800",
      },
    ],
    path: "/trips",
    hideByDefault: false,
    canView: canViewTrips,
  };

  const projectData: MetricCardData = {
    title: PROJECT_TERMINOLOGY.PLURAL,
    total: stats.projectStatus.totalProjects,
    subtitle: PROJECT_TERMINOLOGY.PLURAL,
    data: [
      {
        value: stats.projectStatus.activeProjects,
        label: "Active",
        color: "#4caf50",
      },
      {
        value: stats.projectStatus.completedProjects,
        label: "Completed",
        color: "#ff9800",
      },
    ],
    path:
      planStatus?.operationType === OperationType.LARGE_FLEET ||
      planStatus?.operationType === OperationType.LARGE_FLEET_ALL
        ? `/super`
        : `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}`,
    hideByDefault: false,
  };

  const bookingData: MetricCardData = {
    title: "Bookings",
    total: stats.rentalStatus.totalRentalJourneys,
    subtitle: "Bookings",
    data: [],
    path: "/bookings",
  };

  const allData: MetricCardData = {
    title: "Total Bookings",
    total:
      stats.company.tripCount +
      stats.projectStatus.totalProjects +
      stats.rentalStatus.totalRentalJourneys,
    subtitle: "Total",
    data: [
      {
        value: stats.company.tripCount,
        label: "Daily",
        color: "#4caf50",
      },
      // {
      //   value: stats.projectStatus.totalProjects,
      //   label: "Contracts",
      //   color: "#4caf50",
      // },
      {
        value: stats.rentalStatus.totalRentalJourneys,
        label: "Tour",
        color: "#4caf50",
      },
    ],
  };

  return (
    <>
      {planStatus?.operationType === OperationType.LARGE_FLEET ||
      planStatus?.operationType === OperationType.LARGE_FLEET_ALL ? (
        <LargeFleetDashboard />
      ) : (
        <Container maxWidth="lg" sx={{ py: 2 }}>
          <Grid container spacing={2} sx={{ py: 2, px: 0 }}>
            {paymentDue && (
              <PaymentDue paymentDue={paymentDue} fromDashboard={true} />
            )}
            {/* Metric Cards */}
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard {...vehicleData} />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard {...driverData} />
            </Grid>
            {/* Trip Card */}
            {[OperationType.DAILY_BUS].includes(
              planStatus?.company?.operationType
            ) && (
              <Grid item xs={12} sm={6} md={2.4}>
                <MetricCard {...tripData} />
              </Grid>
            )}
            {[OperationType.CONTRACT, OperationType.SCHOOL].includes(
              planStatus?.operationType
            ) && (
              <Grid item xs={12} sm={6} md={2.4}>
                <MetricCard {...projectData} />
              </Grid>
            )}
            {[OperationType.TOUR_BOOKING].includes(
              planStatus?.operationType
            ) && (
              <Grid item xs={12} sm={6} md={2.4}>
                <MetricCard {...bookingData} />
              </Grid>
            )}
            {[OperationType.ALL].includes(planStatus?.operationType) && (
              <Grid item xs={12} sm={6} md={2.4}>
                <MetricCard {...allData} />
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard
                {...revenueData}
                currency={true}
                hideByDefault={true}
                financeViewPermission={
                  planStatus?.permissions?.canViewFinance || false
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard
                {...expenseData}
                currency={true}
                hideByDefault={true}
                financeViewPermission={
                  planStatus?.permissions?.canViewFinance || false
                }
              />
            </Grid>

            {/* Reports CTA */}
            <Grid item xs={12} md={companyActivity?.length > 0 ? 4 : 7.5}>
              <Paper
                sx={{
                  p: 3,
                  height: { xs: 220, md: 350 },
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  position: "relative",
                  overflow: "hidden",
                  background:
                    "linear-gradient(135deg,rgb(252, 252, 252) 0%,rgb(254, 254, 255) 100%)",
                }}
              >
                {/* Background Icons */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 20,
                    left: 20,
                    opacity: 0.1,
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Assessment sx={{ fontSize: 40 }} />
                  <TrendingUp sx={{ fontSize: 40 }} />
                  <LocalGasStation sx={{ fontSize: 40 }} />
                </Box>
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 20,
                    right: 20,
                    opacity: 0.1,
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Build sx={{ fontSize: 40 }} />
                  <AttachMoney sx={{ fontSize: 40 }} />
                  <Receipt sx={{ fontSize: 40 }} />
                </Box>

                {/* Main Content */}
                <Box sx={{ textAlign: "center", zIndex: 1 }}>
                  {/* Central Icon */}
                  <Assessment
                    className="report-icons"
                    sx={{
                      fontSize: 64,
                      color: "primary.main",
                      mb: 2,
                      transition: "color 0.3s ease-in-out",
                    }}
                  />

                  <Typography
                    variant="h6"
                    className="report-title"
                    sx={{
                      fontWeight: 500,
                      color: "text.primary",
                      mb: 2,
                      fontSize: "1rem",
                      transition: "color 0.3s ease-in-out",
                    }}
                  >
                    MIS/Reports for PNL, Income, Expense, Fuel, Maintenance, and
                    Cashflow
                  </Typography>

                  <Button
                    variant="outlined"
                    color="primary"
                    className="report-button"
                    onClick={() => {
                      if (
                        planStatus?.permissions?.canViewTripPNL ||
                        planStatus?.permissions?.canViewTripLedger ||
                        planStatus?.permissions?.canViewIncomeReport ||
                        planStatus?.permissions?.canViewExpenseReport ||
                        planStatus?.permissions?.canViewFuelReport ||
                        planStatus?.permissions?.canViewMaintenanceReport ||
                        planStatus?.permissions?.canViewCommissionReport ||
                        planStatus?.permissions?.canViewPickupReport ||
                        planStatus?.permissions?.canViewDriverCleanerReport ||
                        planStatus?.permissions?.canViewReportByVehicles
                      ) {
                        navigate("/reports");
                      } else {
                        showToast(
                          "You don't have permission to view reports",
                          "warning"
                        );
                      }
                    }}
                    {...commonButtonProps}
                  >
                    View Reports
                  </Button>
                </Box>
              </Paper>
            </Grid>
            {/* Company Activity */}
            {companyActivity?.length > 0 && (
              <Grid item xs={12} md={4}>
                <CompanyActivity
                  isLoading={isLoading}
                  companyActivity={companyActivity}
                  isLoadingMore={isLoadingMore}
                  hasMore={hasMore}
                  limit={limit}
                  handleShowMore={handleShowMore}
                />
              </Grid>
            )}

            {/* Recent Activities */}
            <Grid item xs={12} md={companyActivity?.length > 0 ? 4 : 4.5}>
              <RecentActivities />
            </Grid>
          </Grid>
        </Container>
      )}
    </>
  );
};

export default Dashboard;
